import { fonts } from "@ui/fonts";
import {
	type Dispatch,
	type SetStateAction,
	startTransition,
	useDeferredValue,
	useEffect,
	useState,
} from "react";
import { saveSettingsColorsAction, saveSettingsFieldAction } from "@/actions/admin";
import { cssOklchToCssHex } from "@/lib/colors";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/ui/shadcn/accordion";
import { Button } from "@/ui/shadcn/button";
import { Label } from "@/ui/shadcn/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/shadcn/select";
import { StatefulColorPicker } from "@/ui/stateful-color-picker";
import type { Settings, SettingsInput } from "@/ui/store-jsonb-schemas";
import { colorPalettes, themeColors } from "./theme-colors";

export const StoreBuilderSettingsForm = ({
	settings,
	setSettings,
}: {
	settings: SettingsInput;
	setSettings: Dispatch<SetStateAction<SettingsInput>>;
}) => {
	const [savedPaletteName, setSavedPaletteName] = useState(settings.colors?.paletteName);
	const [savedColors, setSavedColors] = useState(settings.colors?.palette);
	const deferredColors = useDeferredValue(savedColors);

	useEffect(() => {
		setSettings((settings) => ({
			...settings,
			colors: {
				paletteName: savedPaletteName,
				palette: deferredColors,
			},
		}));
	}, [deferredColors, savedPaletteName, setSettings]);

	return (
		<>
			<h2 className="sr-only">Visual Settings</h2>
			<fieldset className="grid gap-8">
				<Label className="grid gap-1 font-semibold">
					<span>Typography</span>
					<Select
						defaultValue={settings.fontFamily ?? "default"}
						onValueChange={async (value: NonNullable<Settings["fontFamily"]>) => {
							setSettings((settings) => ({
								...settings,
								fontFamily: value,
							}));

							await saveSettingsFieldAction({
								fieldPath: "fontFamily",
								value: JSON.stringify(value),
							});
						}}
					>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="Select your font" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="default" className={""}>
								Default
							</SelectItem>
							<SelectItem value="roboto" className={fonts.roboto.className}>
								Roboto
							</SelectItem>
							<SelectItem value="inter" className={fonts.inter.className}>
								Inter
							</SelectItem>
							<SelectItem value="merriweather" className={fonts.merriweather.className}>
								Merriweather
							</SelectItem>
							<SelectItem value="montserrat" className={fonts.montserrat.className}>
								Montserrat
							</SelectItem>
							<SelectItem value="nunito" className={fonts.nunito.className}>
								Nunito
							</SelectItem>
							<SelectItem value="inconsolata" className={fonts.inconsolata.className}>
								Inconsolata
							</SelectItem>
							<SelectItem value="ibmPlexSans" className={fonts.ibmPlexSans.className}>
								IBM Plex Sans
							</SelectItem>
						</SelectContent>
					</Select>
				</Label>

				<fieldset className="grid gap-4 px-4 pb-5 pt-4 mb-16 border rounded-md">
					<legend className="mb-0 font-semibold text-sm">Color palettes</legend>
					<div className="flex flex-col items-start gap-2">
						<div className="flex flex-row gap-x-4">
							{Object.entries(colorPalettes).map(([paletteName, { palette }]) => {
								return (
									<label
										key={paletteName}
										className="overflow-clip cursor-pointer flex flex-col items-center justify-center border-2 rounded-sm border-transparent has-checked:ring-3 has-checked:ring-indigo-700"
									>
										<span className="text-xs font-light">{paletteName}</span>
										<div className="grid grid-cols-2 grid-rows-2 border">
											<div
												className="w-4 h-4"
												style={{ backgroundColor: `oklch(${palette?.theme?.background})` }}
											/>
											<div
												className="w-4 h-4"
												style={{ backgroundColor: `oklch(${palette?.["theme-primary"]?.DEFAULT})` }}
											/>
											<div
												className="w-4 h-4"
												style={{ backgroundColor: `oklch(${palette?.["theme-primary"]?.background})` }}
											/>
											<div
												className="w-4 h-4"
												style={{ backgroundColor: `oklch(${palette?.["theme-button"]?.DEFAULT})` }}
											/>
										</div>
										<input
											className="sr-only"
											type="radio"
											value={paletteName}
											checked={savedPaletteName === paletteName}
											onChange={() => {
												setSavedPaletteName(paletteName);
												setSavedColors(colorPalettes[paletteName]?.palette);
											}}
											name="palette"
										/>
									</label>
								);
							})}
						</div>
					</div>

					<Accordion type="single" collapsible>
						<AccordionItem value="item-1" className="border-none">
							<AccordionTrigger>Customize colors</AccordionTrigger>
							<AccordionContent>
								<fieldset className="grid max-w-max grid-cols-[auto_1fr] gap-4">
									{Object.entries(themeColors).map(([nodeKey, node]) => {
										return Object.entries(node).map(([leafKey, leaf]) => {
											return (
												<Label
													key={leafKey}
													className="grid col-span-2 cursor-pointer items-center grid-cols-subgrid font-medium"
												>
													<span>{leaf.label}</span>
													<StatefulColorPicker
														name={`${nodeKey}.${leafKey}`}
														onValueChange={(value) => {
															startTransition(() => {
																setSavedPaletteName(undefined);
																setSavedColors((savedColors) => ({
																	...savedColors,
																	[nodeKey]: {
																		...savedColors?.[nodeKey],
																		[leafKey]: value,
																	},
																}));
															});
														}}
														defaultValue={
															cssOklchToCssHex(deferredColors?.[nodeKey]?.[leafKey] ?? "") || "#ffffff"
														}
													/>
												</Label>
											);
										});
									})}
								</fieldset>
							</AccordionContent>
						</AccordionItem>
					</Accordion>

					<Button
						variant="default"
						type="button"
						onClick={async () => {
							await saveSettingsColorsAction({
								colors: {
									paletteName: savedPaletteName,
									palette: savedColors,
								},
							});
						}}
					>
						Apply selected palette
					</Button>
				</fieldset>
			</fieldset>
		</>
	);
};
